<template>
  <z-select ref="select" :name="name" :model-value="value" :disabled="_disabled" :clearable="clearable"
    :clear-icon="clearIcon" :size="size" :effect="effect" :placeholder="placeholder" default-first-option
    :filterable="editable" :empty-values="emptyValues" :value-on-clear="valueOnClear" :popper-class="popperClass"
    :popper-style="popperStyle" @update:model-value="(event) => $emit(UPDATE_MODEL_EVENT, event)"
    @change="(event) => $emit(CHANGE_EVENT, event)" @blur="(event) => $emit('blur', event)"
    @focus="(event) => $emit('focus', event)" @clear="() => $emit('clear')">
    <z-option v-for="item in items" :key="item.value" :label="item.value" :value="item.value"
      :disabled="item.disabled" />
    <template #prefix>
      <z-icon v-if="prefixIcon" :class="nsInput.e('prefix-icon')">
        <component :is="prefixIcon" />
      </z-icon>
    </template>
  </z-select>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import ZSelect from '@zoryn/components/select'
import { useFormDisabled } from '@zoryn/components/form'
import ZIcon from '@zoryn/components/icon'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { CHANGE_EVENT, UPDATE_MODEL_EVENT } from '@zoryn/constants'
import { CircleClose, Clock } from '@zoryn/icons'
import { compareTime, formatTime, nextTime, parseTime } from './utils'
import { debugWarn } from '@zoryn/utils'
import { DEFAULT_STEP } from './time-select'

import type { TimeSelectProps } from './time-select'

dayjs.extend(customParseFormat)

const { Option: ZOption } = ZSelect

defineOptions({
  name: 'ZTimeSelect',
})

defineEmits([CHANGE_EVENT, 'blur', 'focus', 'clear', UPDATE_MODEL_EVENT])

const props = withDefaults(defineProps<TimeSelectProps>(), {
  format: 'HH:mm',
  disabled: undefined,
  editable: true,
  effect: 'light',
  clearable: true,
  start: '09:00',
  end: '18:00',
  step: DEFAULT_STEP,
  prefixIcon: () => Clock,
  clearIcon: () => CircleClose,
  popperClass: '',
  valueOnClear: undefined,
  popperStyle: undefined,
})

const nsInput = useNamespace('input')
const select = ref<typeof ZSelect>()

const _disabled = useFormDisabled()
const { lang } = useLocale()

const value = computed(() => props.modelValue)
const start = computed(() => {
  const time = parseTime(props.start)
  return time ? formatTime(time) : null
})

const end = computed(() => {
  const time = parseTime(props.end)
  return time ? formatTime(time) : null
})

const minTime = computed(() => {
  const time = parseTime(props.minTime || '')
  return time ? formatTime(time) : null
})

const maxTime = computed(() => {
  const time = parseTime(props.maxTime || '')
  return time ? formatTime(time) : null
})

const step = computed(() => {
  const time = parseTime(props.step)
  const isInvalidStep =
    !time ||
    time.hours < 0 ||
    time.minutes < 0 ||
    Number.isNaN(time.hours) ||
    Number.isNaN(time.minutes) ||
    (time.hours === 0 && time.minutes === 0)
  if (isInvalidStep) {
    debugWarn(
      'ZTimeSelect',
      `invalid step, fallback to default step (${DEFAULT_STEP}).`
    )
  }
  return !isInvalidStep ? formatTime(time) : DEFAULT_STEP
})

const items = computed(() => {
  const result: { value: string; rawValue: string; disabled: boolean }[] = []
  const push = (formattedValue: string, rawValue: string) => {
    result.push({
      value: formattedValue,
      rawValue,
      disabled:
        compareTime(rawValue, minTime.value || '-1:-1') <= 0 ||
        compareTime(rawValue, maxTime.value || '100:100') >= 0,
    })
  }

  if (props.start && props.end && props.step) {
    let current = start.value
    let currentTime: string
    while (current && end.value && compareTime(current, end.value) <= 0) {
      currentTime = dayjs(current, 'HH:mm')
        .locale(lang.value)
        .format(props.format)
      push(currentTime, current)
      current = nextTime(current, step.value!)
    }
    if (
      props.includeEndTime &&
      end.value &&
      result[result.length - 1]?.rawValue !== end.value
    ) {
      const formattedValue = dayjs(end.value, 'HH:mm')
        .locale(lang.value)
        .format(props.format)
      push(formattedValue, end.value)
    }
  }
  return result
})

const blur = () => {
  select.value?.blur?.()
}

const focus = () => {
  select.value?.focus?.()
}

defineExpose({
  /**
   * @description blur the Input component
   */
  blur,
  /**
   * @description focus the Input component
   */
  focus,
})
</script>
